<script setup lang="ts">
import { twMerge } from "tailwind-merge";
import { computed, ref, watch } from "vue";
import { initials } from "@/design/initials";
import { tintGradient } from "@/design/tint";

/**
 * Design-system primitive: tokens only, no business knowledge, no network.
 *
 * Um rosto, com a inicial do nome por baixo.
 *
 * ── POR QUE A INICIAL CONTINUA EXISTINDO ──────────────────────────────────
 *
 * Não como desculpa — como resposta certa para quem não subiu foto. Uma silhueta
 * cinza diz menos do que a primeira letra de um nome, e três telas desenhavam a
 * mesma bolinha com o mesmo cálculo. Aqui é um arquivo; o `src` é opcional e cai
 * na inicial sozinho quando não há foto, quando ela ainda está sendo resolvida e
 * quando o arquivo sumiu do storage (`@error`).
 *
 * O `src` chega pronto: quem sabe transformar um id de arquivo num endereço é a
 * porta de upload, e um primitivo que soubesse disso deixaria de ser primitivo.
 *
 * ── DUAS LETRAS, E UMA COR QUE SEGUE A PESSOA ─────────────────────────────
 *
 * Numa equipe com duas Anas, uma letra só não identifica ninguém — é o que faz
 * uma transferência ir para a pessoa errada. E quando o assunto não tem foto nem
 * nunca vai ter (um card de funil é um REGISTRO, não uma conta), a cor sai de um
 * `seed` estável: dois tons de um mesmo hash, o gradiente que o cartão do funil
 * já calculava sozinho. Derivada e não guardada, porque não há coluna de avatar
 * em contato nenhum.
 */
const props = withDefaults(
	defineProps<{
		name?: string | null;
		src?: string | null;
		/** `md` é o tamanho do menu e da lista; `lg`, o do cartão da conta. */
		size?: "xs" | "sm" | "md" | "lg";
		/**
		 * De onde a cor vem, quando não há foto. Um id de registro, um id de
		 * contato — qualquer coisa estável o bastante para a pessoa ter sempre a
		 * mesma cor. Sem ele, a inicial fica no cinza do tema.
		 */
		seed?: string | null;
		class?: string;
	}>(),
	{ name: null, src: null, size: "md", seed: null, class: "" },
);

/**
 * Os nomes não formam uma escada: `sm` (28px) é o da lista de Equipe e nasceu
 * antes, `xs` (32px) é o que a inbox e o funil desenham. Mexer no `sm` para
 * arrumar a ordem mudaria três telas para consertar uma letra.
 */
const SIZE: Record<NonNullable<typeof props.size>, string> = {
	xs: "size-8 text-2xs",
	sm: "size-7 text-2xs",
	md: "size-9 text-sm",
	lg: "size-12 text-base",
};

const broken = ref(false);
// Uma foto nova tem de poder substituir uma que falhou: sem isto, trocar o
// arquivo depois de um erro deixaria a inicial para sempre.
watch(
	() => props.src,
	() => {
		broken.value = false;
	},
);

const initial = computed(() => initials(props.name));

const shown = computed(() => (props.src && !broken.value ? props.src : null));

/**
 * Dois tons de um mesmo hash — a V2 tinha tabela de paleta para manter em dia.
 *
 * A conta mora em `design/tint.ts` porque ela tem uma segunda ponta: a pílula de
 * etiqueta do funil tinge-se do MESMO matiz do mesmo texto. Duas cópias do hash
 * divergiriam no primeiro dia em que alguém mexesse numa delas.
 */
const tint = computed(() => (!props.seed || shown.value ? undefined : tintGradient(props.seed)));

const classes = computed(() =>
	twMerge(
		"flex shrink-0 items-center justify-center overflow-hidden rounded-full font-semibold",
		tint.value ? "text-white" : "bg-accent-subtle text-accent",
		SIZE[props.size],
		props.class,
	),
);
</script>

<template>
	<span :class="classes" :style="tint ? { background: tint } : undefined" aria-hidden="true">
		<img
			v-if="shown"
			:src="shown"
			alt=""
			class="size-full object-cover"
			@error="broken = true"
		/>
		<template v-else>{{ initial }}</template>
	</span>
</template>
